import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Filter, RefreshCw, Eye, Calendar } from "lucide-react";
import { formatDate, formatTime, todayISO, todayDisplay as getTodayDisplay } from "@/lib/dateTime";
import { PageHeader } from "@/components/PageHeader";
import { useColumnManager } from "@/hooks/useColumnManager";
import { ColumnModals } from "@/components/ColumnModals";
import {
  DEFAULT_APPOINTMENT_COLUMNS,
  CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS,
  getAppointmentColumns,
} from "@/lib/appointmentColumns";

// Column set shared with Upcoming (Tomorrow + Future) and History.
// Today's status column is the Day's Outcome.
const COLUMNS = getAppointmentColumns("Day's Outcome");

const DayView = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [rescheduledCancelled, setRescheduledCancelled] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    branch: "",
    status: "",
    outcome: "",
    cre: "",
    sa: "",
    source: "",
  });
  const [cres, setCres] = useState([]);
  const [branches, setBranches] = useState([]);

  // Column visibility + ordering — persisted per-user under the "today" pref key.
  const columnManager = useColumnManager(
    "today",
    COLUMNS,
    CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS
  );
  const { visibleCols } = columnManager;

  const today = todayISO();
  const todayDisplayStr = getTodayDisplay();

  // Get all reschedule history entries
  const getRescheduleEntries = (history) => (history || []).filter(h => h.from_date);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({ view: "day", date: today });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const [apptsRes, settingsRes, cresRes, branchesRes] = await Promise.all([
        axios.get(`${API}/appointments?${params}`, { withCredentials: true }),
        axios.get(`${API}/settings`, { withCredentials: true }),
        axios.get(`${API}/users/cres`, { withCredentials: true }),
        axios.get(`${API}/branches`, { withCredentials: true }),
      ]);

      // Separate regular appointments from rescheduled/cancelled in N-1
      const regular = apptsRes.data.filter(apt => 
        !apt.rescheduled_in_n1 && !apt.cancelled_in_n1
      );
      const reschCanc = apptsRes.data.filter(apt => 
        apt.rescheduled_in_n1 || apt.cancelled_in_n1
      );

      // Sort by time ascending
      regular.sort((a, b) => (a.appointment_time || "").localeCompare(b.appointment_time || ""));
      reschCanc.sort((a, b) => (a.appointment_time || "").localeCompare(b.appointment_time || ""));

      setAppointments(regular);
      setRescheduledCancelled(reschCanc);
      setSettings(settingsRes.data);
      setCres(cresRes.data);
      setBranches(branchesRes.data || []);
    } catch (error) {
      toast.error("Failed to load appointments");
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (key, v) => {
    setFilters((prev) => ({ ...prev, [key]: v === "all" ? "" : v }));
  };

  // Cell renderer
  const renderCell = (colId, appt) => {
    switch (colId) {
      case "time":
        return <span className="text-sm font-medium">{formatTime(appt.appointment_time)}</span>;
      case "source":
        return <span className="text-sm">{appt.source}</span>;
      case "customer_name":
        return (
          <div className="flex items-center gap-2">
            <span className="font-medium">{appt.customer_name}</span>
            {appt.priority_customer && (
              <div className="w-5 h-5 bg-black text-white rounded-sm flex items-center justify-center text-xs font-bold shrink-0">P</div>
            )}
            {appt.is_rescheduled && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="w-5 h-5 bg-amber-100 text-amber-800 rounded-sm flex items-center justify-center text-xs font-bold shrink-0 cursor-help" data-testid={`reschedule-badge-${appt.appointment_id}`}>R</div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[240px] p-3 bg-gray-100 border-0">
                    {(() => {
                      const entries = getRescheduleEntries(appt.reschedule_history);
                      return entries.length > 0 ? (
                        <>
                          <p className="font-bold text-sm mb-2 text-black">{entries.length} reschedule{entries.length !== 1 ? "s" : ""}</p>
                          {entries.map((h, i) => (
                            <p key={i} className="text-sm text-black mb-1">&gt; {formatDate(h.from_date)}</p>
                          ))}
                        </>
                      ) : (
                        <p className="text-sm text-black">Rescheduled</p>
                      );
                    })()}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        );
      case "phone":
        return <span className="text-sm">{appt.customer_phone}</span>;
      case "mail_id":
        return <span className="text-sm">{appt.customer_email || "-"}</span>;
      case "reg_no":
        return <span className="text-sm font-medium">{appt.vehicle_reg_no || appt.vehicle_reg || "-"}</span>;
      case "vin":
        return <span className="text-sm">{appt.vin || "-"}</span>;
      case "model":
        return <span className="text-sm">{appt.vehicle_model || appt.model}</span>;
      case "current_km":
        return <span className="text-sm">{appt.current_km || "-"}</span>;
      case "ots":
        return <span className="text-sm">{appt.ots || appt.ots_recall ? "Yes" : "No"}</span>;
      case "service_type":
        return <Badge variant="outline" className="rounded-sm text-xs font-medium whitespace-nowrap">{appt.service_type}</Badge>;
      case "sa_name":
        return <span className="text-sm">{appt.allocated_sa}</span>;
      case "docket_readiness":
        return (
          <Badge className={`rounded-sm text-xs font-medium border-0 whitespace-nowrap ${appt.docket_readiness ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
            {appt.docket_readiness ? "Yes" : "No"}
          </Badge>
        );
      case "n1":
        return <span className="text-xs">{appt.n_minus_1_confirmation || appt.n_minus_1_confirmation_status || "Pending"}</span>;
      case "status":
        return <span className="text-xs">{appt.appointment_day_outcome || "Pending"}</span>;
      case "cre_name":
        return <span className="text-sm">{appt.cre_name}</span>;
      case "lost_customer":
        return (
          <Badge className={`rounded-sm text-xs font-medium border-0 whitespace-nowrap ${appt.lost_customer ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"}`}>
            {appt.lost_customer ? "Yes" : "No"}
          </Badge>
        );
      case "remarks":
        return appt.specific_repair || appt.specific_repair_request ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs font-medium cursor-help underline decoration-dotted">Yes</span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs"><p className="text-sm">{appt.specific_repair || appt.specific_repair_request}</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : <span className="text-xs text-gray-400">No</span>;
      case "actions":
        return (
          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => navigate(`/appointments/${appt.appointment_id}`)} data-testid={`action-btn-${appt.appointment_id}`}>
            <Eye className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        );
      default:
        return null;
    }
  };

  const AppointmentTable = ({ appointments, showEmpty = true }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="table-header bg-gray-50">
            {visibleCols.map((col) => (
              <TableHead key={col.id} className={`text-xs font-bold uppercase whitespace-nowrap text-center${col.id === "actions" ? " w-12" : ""}`}>
                {col.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {appointments.length === 0 && showEmpty ? (
            <TableRow>
              <TableCell colSpan={visibleCols.length} className="text-center text-gray-400 py-8">
                No appointments
              </TableCell>
            </TableRow>
          ) : (
            appointments.map((appt) => (
              <TableRow key={appt.appointment_id} className="hover:bg-gray-50">
                {visibleCols.map((col) => (
                  <TableCell key={col.id} className={`whitespace-nowrap text-center${col.id === "customer_name" ? " text-left" : ""}`}>
                    {renderCell(col.id, appt)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );

  // Filter Dialog - same pattern as History.jsx
  const FilterDialog = () => (
    <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
      <DialogContent className="rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
            Filters
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 pt-4">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Branch
            </Label>
            <Select value={filters.branch || "all"} onValueChange={(v) => updateFilter("branch", v)}>
              <SelectTrigger className="rounded-sm" data-testid="filter-branch">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.branch_id} value={b.location}>{b.location}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Status
            </Label>
            <Select value={filters.status || "all"} onValueChange={(v) => updateFilter("status", v)}>
              <SelectTrigger className="rounded-sm" data-testid="filter-status">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {settings?.appointment_statuses?.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Outcome
            </Label>
            <Select value={filters.outcome || "all"} onValueChange={(v) => updateFilter("outcome", v)}>
              <SelectTrigger className="rounded-sm" data-testid="filter-outcome">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Outcomes</SelectItem>
                {settings?.appointment_day_outcomes?.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Source
            </Label>
            <Select value={filters.source || "all"} onValueChange={(v) => updateFilter("source", v)}>
              <SelectTrigger className="rounded-sm" data-testid="filter-source">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {settings?.sources?.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Service Advisor
            </Label>
            <Select value={filters.sa || "all"} onValueChange={(v) => updateFilter("sa", v)}>
              <SelectTrigger className="rounded-sm" data-testid="filter-sa">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All SAs</SelectItem>
                {settings?.service_advisors?.map((sa) => (
                  <SelectItem key={sa} value={sa}>{sa}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              CRE
            </Label>
            <Select value={filters.cre || "all"} onValueChange={(v) => updateFilter("cre", v)}>
              <SelectTrigger className="rounded-sm" data-testid="filter-cre">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All CREs</SelectItem>
                {cres.map((c) => (
                  <SelectItem key={c.user_id} value={c.user_id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => {
              setFilters({ branch: "", status: "", outcome: "", cre: "", sa: "", source: "" });
            }}
            data-testid="filter-clear"
          >
            Clear All
          </Button>
          <Button
            className="bg-black text-white hover:bg-gray-800 rounded-sm"
            onClick={() => setFilterOpen(false)}
            data-testid="filter-apply"
          >
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="day-view-page">
      {/* Standard Header */}
      <PageHeader
        icon={Calendar}
        title="Today"
        subtitle={todayDisplayStr}
        onFilter={() => setFilterOpen(true)}
        onRefresh={fetchData}
        columnControls={{
          onCustomize: columnManager.openCustomize,
          onRearrange: () => columnManager.openRearrange(),
        }}
      />

      {/* Main Today Section */}
      <div className="space-y-3">
        <div className="text-sm text-gray-600 font-medium">
          {appointments.length} appointment{appointments.length !== 1 ? 's' : ''}
        </div>
        <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
          <AppointmentTable appointments={appointments} />
        </Card>
      </div>

      {/* Rescheduled / Cancelled in N-1 Section */}
      {rescheduledCancelled.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-heading font-bold text-xl tracking-tight uppercase">
            Rescheduled / Cancelled in N-1
          </h2>
          <div className="text-sm text-gray-600 font-medium">
            {rescheduledCancelled.length} appointment{rescheduledCancelled.length !== 1 ? 's' : ''}
          </div>
          <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
            <AppointmentTable appointments={rescheduledCancelled} showEmpty={false} />
          </Card>
        </div>
      )}

      {/* Filter Dialog */}
      <FilterDialog />

      {/* Customize + Rearrange modals (shared) */}
      <ColumnModals
        columnManager={columnManager}
        defaultColumns={COLUMNS}
        customizableIds={CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS}
      />
    </div>
  );
};

export default DayView;
